import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { GraduationCap, Loader2, AlertCircle, Upload, ArrowLeft, CheckCircle2, PlayCircle, Circle, Sparkles, X, ExternalLink } from "lucide-react";
import { Scorm12API } from "scorm-again";
import { toast } from "sonner";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { CourseCover } from "../components/CourseCover";

/**
 * Modulo Formazione — Catalogo multi-corso SCORM 1.2 (PARTE B v4).
 * Griglia dei corsi assegnati al tenant → click → player SCORM per corso.
 */

const STATUS_BADGE = {
  completed:   { label: "Completato",  cls: "wl-badge wl-badge--success", Icon: CheckCircle2 },
  in_progress: { label: "In corso",    cls: "wl-badge wl-badge--info",    Icon: PlayCircle },
  not_started: { label: "Da iniziare", cls: "wl-badge wl-badge--neutral", Icon: Circle },
};

const CourseCard = ({ course, onOpen }) => {
  const badge = STATUS_BADGE[course.my_status] || STATUS_BADGE.not_started;
  const BadgeIcon = badge.Icon;
  return (
    <button
      type="button"
      onClick={() => onOpen(course)}
      data-testid={`course-card-${course.id}`}
      className="text-left bg-surface border border-border rounded-xl overflow-hidden hover:border-accent transition-colors group flex flex-col"
    >
      <div className="h-32 overflow-hidden">
        <CourseCover
          courseId={course.id}
          hasCover={course.has_cover}
          title={course.title}
          version={course.uploaded_at}
          className="group-hover:scale-[1.02] transition-transform duration-300"
          testId={`course-cover-${course.id}`}
        />
      </div>
      <div className="p-4 flex-1 flex flex-col gap-2">
        <div className="flex items-center gap-2 flex-wrap">
          {course.is_new && (
            <span className="wl-badge wl-badge--warning" style={{ fontSize: "10px" }} data-testid={`course-new-${course.id}`}>
              <Sparkles size={10} strokeWidth={2.5} /> Nuovo
            </span>
          )}
          {course.category && (
            <span className="wl-badge wl-badge--neutral" style={{ fontSize: "10px" }} data-testid={`course-category-${course.id}`}>
              {course.category}
            </span>
          )}
          {course.course_type === "link_esterno" ? (
            <span className="wl-badge wl-badge--neutral" style={{ fontSize: "10px" }} data-testid={`course-external-${course.id}`}>
              <ExternalLink size={10} strokeWidth={2.5} /> Corso esterno
            </span>
          ) : (
            <span className={badge.cls} style={{ fontSize: "10px" }} data-testid={`course-status-${course.id}`}>
              <BadgeIcon size={10} strokeWidth={2.5} /> {badge.label}
            </span>
          )}
        </div>
        <div className="text-sm font-semibold" style={{ color: "var(--wl-ink)" }}>{course.title}</div>
        {course.description && (
          <div className="text-xs leading-relaxed line-clamp-2" style={{ color: "var(--wl-text-secondary)" }}>
            {course.description}
          </div>
        )}
      </div>
    </button>
  );
};

const ScormPlayer = ({ course, onBack }) => {
  const [status, setStatus] = useState("loading"); // loading | ready | error
  const [errorMsg, setErrorMsg] = useState("");
  const [iframeSrc, setIframeSrc] = useState(null);
  const [external, setExternal] = useState(false);
  const scormRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    let scormInstance = null;

    (async () => {
      try {
        const { data: cfg } = await api.get(`/formazione/courses/${course.id}/config`);
        if (cancelled) return;

        if (cfg.course_type === "link_esterno") {
          // Corso su sito terzo: iframe verso l'URL, nessun tracciamento SCORM.
          setExternal(true);
          setIframeSrc(cfg.external_url);
          setStatus("ready");
          return;
        }

        const token = localStorage.getItem("wattline_token") || "";
        const backendUrl = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");
        const separator = cfg.entry_url.includes("?") ? "&" : "?";
        const src = `${backendUrl}${cfg.entry_url}${separator}access_token=${encodeURIComponent(token)}`;
        // Cookie limitato al path del corso: gli asset relativi del pacchetto
        // (JS/CSS/immagini/pagine) non possono portare l'header Authorization.
        document.cookie = `access_token=${token}; path=/api/formazione/scorm/${course.id}/; SameSite=Strict; Secure`;

        const commitUrl = `${backendUrl}/api/formazione/progress?session=${encodeURIComponent(cfg.session_id)}&course=${encodeURIComponent(course.id)}`;
        scormInstance = new Scorm12API({
          lmsCommitUrl: commitUrl,
          autocommit: true,
          autocommitSeconds: 30,
          logLevel: 4,
          xhrHeaders: {
            "Authorization": `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          xhrWithCredentials: false,
          responseHandler: async (response) => {
            try {
              const j = await response.json();
              return { result: j.ok ? "true" : "false", errorCode: j.ok ? 0 : 101 };
            } catch (e) {
              return { result: "false", errorCode: 101 };
            }
          },
          commitRequestDataType: "application/json",
          renderCommonCommitFields: false,
        });

        try {
          scormInstance.cmi.core.student_id = cfg.student_id || "";
          scormInstance.cmi.core.student_name = cfg.student_name || "Utente";
          scormInstance.cmi.core.lesson_status = "not attempted";
        } catch (e) { /* struttura API differente: init può comunque riuscire */ }

        window.API = scormInstance;

        if (!cancelled) {
          scormRef.current = scormInstance;
          setIframeSrc(src);
          setStatus("ready");
        }
      } catch (e) {
        console.error("[Formazione] player init failed", e);
        if (!cancelled) {
          setErrorMsg(e?.response?.data?.detail || "Errore nel caricamento del corso.");
          setStatus("error");
        }
      }
    })();

    return () => {
      cancelled = true;
      document.cookie = `access_token=; path=/api/formazione/scorm/${course.id}/; expires=Thu, 01 Jan 1970 00:00:00 GMT; SameSite=Strict; Secure`;
      try {
        if (window.API && typeof window.API.lmsFinish === "function") {
          window.API.lmsFinish("");
        }
      } catch (e) { /* noop */ }
      try { delete window.API; } catch (e) { /* noop */ }
    };
  }, [course.id]);

  return (
    <div className="wl-page">
      <div className="wl-page__header" style={{ marginBottom: "16px" }}>
        <button
          type="button"
          onClick={onBack}
          className="wl-btn wl-btn--ghost wl-btn--sm mb-2"
          style={{ border: "1px solid var(--wl-border)" }}
          data-testid="player-back-to-catalog"
        >
          <ArrowLeft size={13} /> Tutti i corsi
        </button>
        <h1 className="wl-page__title">{course.title}</h1>
      </div>

      <div className="wl-formazione-wrapper">
        {status === "loading" && (
          <div className="wl-formazione-empty">
            <Loader2 size={20} className="animate-spin" style={{ color: "var(--wl-text-secondary)" }} />
            <span className="text-sm" style={{ color: "var(--wl-text-secondary)" }}>Preparo il corso…</span>
          </div>
        )}
        {status === "error" && (
          <div className="wl-formazione-empty">
            <AlertCircle size={28} style={{ color: "var(--wl-danger)" }} />
            <div>
              <div className="text-sm font-medium" style={{ color: "var(--wl-ink)" }}>Impossibile caricare il corso</div>
              <div className="text-xs mt-1" style={{ color: "var(--wl-text-secondary)" }}>{errorMsg}</div>
            </div>
          </div>
        )}
        {status === "ready" && iframeSrc && (
          <>
            {external && (
              <div className="flex items-center justify-between gap-2 flex-wrap px-3 py-2 mb-2 rounded-lg border border-border bg-subtle text-xs"
                   style={{ color: "var(--wl-text-secondary)" }} data-testid="external-course-note">
                <span>Corso esterno: si apre da un sito terzo, i progressi non vengono tracciati in Wattline.</span>
                <a href={iframeSrc} target="_blank" rel="noreferrer"
                   className="wl-btn wl-btn--ghost wl-btn--sm shrink-0"
                   style={{ border: "1px solid var(--wl-border)" }}
                   data-testid="external-open-newtab">
                  <ExternalLink size={12} /> Apri in una scheda
                </a>
              </div>
            )}
            <iframe
              src={iframeSrc}
              title={course.title || "Corso di formazione"}
              className="wl-formazione-iframe"
              allow="fullscreen"
              allowFullScreen
              data-testid="formazione-iframe"
            />
          </>
        )}
      </div>
    </div>
  );
};

const AREA_SUGGESTIONS = [
  "Back office energia", "Onboarding nuovi assunti", "Uso degli strumenti AI",
  "Sicurezza e privacy", "Normativa di settore", "Altro",
];
const URGENCY_OPTIONS = [
  { id: "alta", label: "Il prima possibile" },
  { id: "media", label: "Entro un mese" },
  { id: "bassa", label: "Senza fretta" },
];
const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString("it-IT") : "");

const TrainingRequestModal = ({ onClose, onSent }) => {
  const [area, setArea] = useState("");
  const [audience, setAudience] = useState("");
  const [goal, setGoal] = useState("");
  const [urgency, setUrgency] = useState("media");
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    if (area.trim().length < 2) { toast.error("Indica l'area o il tema del corso"); return; }
    if (goal.trim().length < 5) { toast.error("Descrivi in 2-3 righe l'obiettivo del corso"); return; }
    setSaving(true);
    try {
      const { data } = await api.post("/formazione/requests", { area, audience, goal, urgency });
      toast.success("Richiesta inviata: prepariamo il percorso e ti ricontattiamo.");
      onSent(data);
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Invio non riuscito, riprova.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="wl-modal-overlay" onClick={() => !saving && onClose()}>
      <div className="wl-modal wl-modal--md" role="dialog" aria-modal="true" onClick={(e) => e.stopPropagation()}>
        <div className="wl-modal__header">
          <div className="wl-modal__icon wl-modal__icon--info" aria-hidden="true"><GraduationCap size={18} /></div>
          <div className="wl-modal__title-block">
            <h3 className="wl-modal__title">Richiedi un percorso formativo</h3>
            <p className="wl-modal__desc">Raccontaci cosa serve al tuo team: costruiamo il corso su misura per voi.</p>
          </div>
          <button type="button" onClick={() => !saving && onClose()} className="text-text-muted hover:text-text-primary" aria-label="Chiudi"><X size={16} /></button>
        </div>
        <div className="wl-modal__body space-y-3">
          <div className="wl-field">
            <label className="wl-label">Area o tema del corso *</label>
            <input value={area} onChange={(e) => setArea(e.target.value)} maxLength={120}
                   placeholder="es. Gestione reclami clienti energia" className="wl-input"
                   data-testid="training-request-area" />
            <div className="flex items-center gap-1.5 flex-wrap mt-2">
              {AREA_SUGGESTIONS.map((s) => (
                <button key={s} type="button" onClick={() => setArea(s === "Altro" ? "" : s)}
                        className={`text-xs px-2.5 py-1 rounded-full border transition-colors ${area === s ? "border-accent bg-accent-subtle" : "border-border hover:border-accent"}`}
                        style={{ color: "var(--wl-text-secondary)" }}
                        data-testid={`training-suggestion-${s.toLowerCase().replace(/\s+/g, "-")}`}>
                  {s}
                </button>
              ))}
            </div>
          </div>
          <div className="wl-field">
            <label className="wl-label">Destinatari</label>
            <input value={audience} onChange={(e) => setAudience(e.target.value)} maxLength={200}
                   placeholder="Quante persone e che ruoli (es. 5 operatori back office)"
                   className="wl-input" data-testid="training-request-audience" />
          </div>
          <div className="wl-field">
            <label className="wl-label">Obiettivo *</label>
            <textarea value={goal} onChange={(e) => setGoal(e.target.value)} maxLength={600} rows={3}
                      placeholder="In 2-3 righe: cosa deve saper fare il team alla fine del corso"
                      className="wl-textarea" data-testid="training-request-goal" />
          </div>
          <div className="wl-field">
            <label className="wl-label">Urgenza indicativa</label>
            <div className="flex gap-2 flex-wrap">
              {URGENCY_OPTIONS.map((u) => (
                <button key={u.id} type="button" onClick={() => setUrgency(u.id)}
                        className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${urgency === u.id ? "border-accent bg-accent-subtle font-medium" : "border-border"}`}
                        style={{ color: urgency === u.id ? "var(--wl-ink)" : "var(--wl-text-secondary)" }}
                        data-testid={`training-urgency-${u.id}`}>
                  {u.label}
                </button>
              ))}
            </div>
          </div>
        </div>
        <div className="wl-modal__actions">
          <button type="button" onClick={onClose} disabled={saving} className="wl-btn wl-btn--ghost">Annulla</button>
          <button type="button" onClick={submit} disabled={saving} className="wl-btn wl-btn--primary"
                  data-testid="training-request-submit">
            {saving && <Loader2 size={14} className="animate-spin" />} Invia richiesta
          </button>
        </div>
      </div>
    </div>
  );
};

export default function Formazione() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const isSuperAdmin = user?.role === "super_admin";
  const [courses, setCourses] = useState(null); // null = loading
  const [error, setError] = useState("");
  const [errorIs403, setErrorIs403] = useState(false);
  const [active, setActive] = useState(null);
  const [myRequest, setMyRequest] = useState(null);
  const [showRequest, setShowRequest] = useState(false);

  const apriCorso = (c) => {
    if (c.is_manuale && c.slug) navigate(`/formazione/${c.slug}`);
    else setActive(c);
  };

  const load = async () => {
    setError("");
    try {
      const { data } = await api.get("/formazione/courses");
      setCourses(data.courses || []);
    } catch (e) {
      const is403 = e?.response?.status === 403;
      setErrorIs403(is403);
      setError(is403
        ? (e?.response?.data?.detail || "Il modulo Formazione non è attivo per la tua organizzazione.")
        : "Impossibile caricare il catalogo: il collegamento col server non ha risposto. È un problema temporaneo, i corsi non sono andati persi.");
      setCourses([]);
    }
    if (!isSuperAdmin) {
      try {
        const { data: r } = await api.get("/formazione/requests/mine");
        setMyRequest(r.request || null);
      } catch (e) { /* noop */ }
    }
  };
  useEffect(() => { load(); }, []);

  if (active) {
    return <ScormPlayer course={active} onBack={() => { setActive(null); load(); }} />;
  }

  return (
    <div className="wl-page">
      <div className="wl-page__header" style={{ marginBottom: "16px" }}>
        <div className="wl-page__eyebrow">
          <GraduationCap size={12} strokeWidth={2} style={{ marginRight: 4, verticalAlign: "-1px" }} />
          Formazione
        </div>
        <h1 className="wl-page__title">I tuoi corsi</h1>
        <p className="wl-page__meta">I corsi assegnati alla tua azienda, con i progressi tracciati automaticamente.</p>
      </div>

      {courses === null ? (
        <div className="wl-formazione-empty">
          <Loader2 size={20} className="animate-spin" style={{ color: "var(--wl-text-secondary)" }} />
          <span className="text-sm" style={{ color: "var(--wl-text-secondary)" }}>Carico il catalogo…</span>
        </div>
      ) : error ? (
        <div className="wl-formazione-empty" data-testid="formazione-load-error">
          <AlertCircle size={28} style={{ color: "var(--wl-danger)" }} />
          <div className="text-sm" style={{ color: "var(--wl-text-secondary)" }}>{error}</div>
          {!errorIs403 && (
            <button
              type="button"
              onClick={load}
              data-testid="formazione-retry"
              className="mt-2 inline-flex items-center gap-1.5 rounded-md border border-border bg-surface px-3 py-1.5 text-xs font-medium hover:bg-subtle transition-colors"
              style={{ color: "var(--wl-ink)" }}
            >
              Riprova
            </button>
          )}
        </div>
      ) : courses.length === 0 ? (
        <div className="wl-formazione-empty" data-testid="formazione-empty">
          <div className="wl-formazione-empty__icon">
            <GraduationCap size={28} strokeWidth={1.5} />
          </div>
          {isSuperAdmin ? (
            <>
              <div>
                <div className="text-base font-medium" style={{ color: "var(--wl-ink)" }}>
                  Nessun corso nel catalogo.
                </div>
                <div className="text-sm mt-2" style={{ color: "var(--wl-text-secondary)" }}>
                  Carica i corsi SCORM e assegnali ai tenant dal Catalogo formazione.
                </div>
              </div>
              <Link
                to="/superadmin/formazione"
                className="wl-btn wl-btn--primary mt-2"
                data-testid="formazione-goto-upload"
              >
                <Upload size={14} /> Gestisci catalogo
              </Link>
            </>
          ) : (
            <>
              <div>
                <div className="text-base font-semibold" style={{ color: "var(--wl-ink)" }}>
                  La tua formazione, su misura.
                </div>
                <div className="text-sm mt-2 mx-auto" style={{ color: "var(--wl-text-secondary)", maxWidth: "420px" }}>
                  Prepariamo i corsi sulle esigenze della tua azienda: dicci cosa ti serve e lo costruiamo per te.
                </div>
              </div>
              {myRequest ? (
                <div className="wl-badge wl-badge--info" data-testid="training-request-sent"
                     style={{ padding: "8px 14px", fontSize: "12px" }}>
                  <CheckCircle2 size={12} /> Richiesta inviata il {fmtDate(myRequest.created_at)} — ti ricontattiamo a breve
                </div>
              ) : (
                <button type="button" onClick={() => setShowRequest(true)}
                        className="wl-btn wl-btn--primary mt-2" data-testid="training-request-open">
                  <Sparkles size={14} /> Richiedi un percorso formativo
                </button>
              )}
            </>
          )}
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="formazione-catalog">
          {courses.map((c) => (
            <CourseCard key={c.id} course={c} onOpen={apriCorso} />
          ))}
        </div>
      )}

      {showRequest && (
        <TrainingRequestModal onClose={() => setShowRequest(false)} onSent={(r) => setMyRequest(r)} />
      )}
    </div>
  );
}
